import { useState, useEffect } from 'react';
import { getActivityFeed } from '../api/dashboard';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import {
  FileText,
  Filter,
  Download,
  Search,
  Calendar,
  User,
  Activity
} from 'lucide-react';
import Pagination from '../components/common/Pagination';

const ActivityLogsPage = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activityFilter, setActivityFilter] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });

  useEffect(() => {
    loadActivities();
  }, [pagination.offset, activityFilter, dateRange]);

  const loadActivities = async () => {
    try {
      setLoading(true);
      const params = {
        limit: pagination.limit,
        offset: pagination.offset
      };

      if (activityFilter) {
        params.activity_type = activityFilter;
      }

      if (dateRange.start) {
        params.start_date = dateRange.start;
      }

      if (dateRange.end) {
        params.end_date = dateRange.end;
      }

      const response = await getActivityFeed(params);

      if (response.success) {
        setActivities(response.data || []);
      }
    } catch (error) {
      console.error('Error loading activity logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination({ ...pagination, offset: 0 });
    loadActivities();
  };

  const handleReset = () => {
    setSearchTerm('');
    setActivityFilter('');
    setDateRange({ start: '', end: '' });
    setPagination({ ...pagination, offset: 0 });
  };

  const handleExport = () => {
    toast.info('Export functionality coming soon');
  };

  const filteredActivities = activities.filter(activity =>
    searchTerm === '' ||
    activity.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.action_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (actionType) => {
    const type = actionType?.toLowerCase() || '';
    if (type.includes('create')) return '➕';
    if (type.includes('update')) return '✏️';
    if (type.includes('delete')) return '🗑️';
    if (type.includes('login')) return '🔑';
    if (type.includes('logout')) return '🚪';
    return '📝';
  };

  const getActivityColor = (actionType) => {
    const type = actionType?.toLowerCase() || '';
    if (type.includes('create')) return 'text-green-600 bg-green-50';
    if (type.includes('update')) return 'text-blue-600 bg-blue-50';
    if (type.includes('delete')) return 'text-red-600 bg-red-50';
    if (type.includes('login')) return 'text-purple-600 bg-purple-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-primary-black flex items-center gap-2">
            <FileText className="w-6 h-6 md:w-8 md:h-8 text-accent-gold" />
            System Activity Logs
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">
            {user.role === 'staff'
              ? 'View your activity history'
              : 'Monitor all system activities and user actions'}
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm md:text-base bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors w-full md:w-auto"
        >
          <Download className="w-4 h-4" />
          Export Logs
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div className="md:col-span-2">
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
              <Search className="w-3 h-3 md:w-4 md:h-4 inline mr-1" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by description, user, or action..."
              className="w-full px-3 md:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-3 h-3 md:w-4 md:h-4 inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className="w-full px-3 md:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-3 h-3 md:w-4 md:h-4 inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className="w-full px-3 md:px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent-gold focus:border-transparent"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            onClick={handleSearch}
            className="w-full sm:w-auto px-4 py-2 text-sm md:text-base bg-accent-gold text-white rounded-lg hover:bg-accent-gold/90 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={handleReset}
            className="w-full sm:w-auto px-4 py-2 text-sm md:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Activity Logs */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold"></div>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No activity logs found</p>
          </div>
        ) : (
          <>
            {/* Mobile View - Cards */}
            <div className="block lg:hidden divide-y divide-gray-200">
              {filteredActivities.map((activity) => (
                <div key={activity.id} className="p-4 hover:bg-gray-50 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getActivityColor(activity.action_type)}`}>
                      <span className="mr-1">{getActivityIcon(activity.action_type)}</span>
                      {activity.action_type || 'N/A'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(activity.created_at)}
                    </span>
                  </div>

                  {/* User Info */}
                  <div className="flex items-center mb-2">
                    <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {activity.user_name || 'System'}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {activity.user_email}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="text-sm text-gray-900 mb-2">
                    {activity.description || 'No description'}
                  </div>

                  {/* Target User */}
                  {activity.target_user_name && (
                    <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                      <span className="text-gray-500">Affected User:</span>
                      <span className="font-medium">{activity.target_user_name}</span>
                    </div>
                  )}

                  {/* Entity */}
                  {activity.entity_display && (
                    <div className="text-xs text-gray-500">
                      Entity: {activity.entity_display}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop View - Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entity
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredActivities.map((activity) => (
                    <tr key={activity.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(activity.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {activity.user_name || 'System'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {activity.user_email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getActivityColor(activity.action_type)}`}>
                          <span className="mr-1">{getActivityIcon(activity.action_type)}</span>
                          {activity.action_type || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div>{activity.description || 'No description'}</div>
                        {activity.target_user_name && (
                          <div className="text-xs text-gray-600 mt-1">
                            <span className="text-gray-500">Affected:</span>{' '}
                            <span className="font-medium">{activity.target_user_name}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {activity.entity_display || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* Pagination */}
        {!loading && filteredActivities.length > 0 && (
          <Pagination
            currentPage={Math.floor(pagination.offset / pagination.limit) + 1}
            totalPages={Math.ceil(filteredActivities.length / pagination.limit)}
            totalItems={filteredActivities.length}
            itemsPerPage={pagination.limit}
            onPageChange={(page) => setPagination({ ...pagination, offset: (page - 1) * pagination.limit })}
            itemLabel="activities"
          />
        )}
      </div>
    </div>
  );
};

export default ActivityLogsPage;
